<template>
  <div class="relative flex  md:h-[600px]h-[425px] w-full flex-col items-center justify-center overflow-x-hidden rounded-lg md:py-0">
    <!-- Row 1 -->
    <Marquee
        pause-on-hover
        class="[--duration:30s] mb-1"
    >
      <div
          v-for="leak in firstRow"
          :key="leak.id"
          class="mx-2 bg-white rounded-md border border-gray-200 p-3 min-w-[400px] hover:bg-gray-accent transition-colors"
      >
        <div class="flex items-center space-x-3">
          <img :src="leak.favicon" :alt="leak.alt" class="w-5 h-5 flex-shrink-0">
          <div class="flex-1 min-w-0">
            <p class="font-semibold text-gray-800 truncate text-sm">{{ leak.title }}</p>
            <p class="text-gray-500 truncate text-xs">{{ leak.url }}</p>
          </div>
          <div class="flex items-center space-x-2">
            <span class="text-xs font-semibold px-2 py-1 rounded-full bg-secondary-400 text-white">{{ locale === 'de' ? 'Entfernt' : 'Removed' }}</span>
          </div>
        </div>
        <div class="mt-2 flex items-center justify-between text-xs text-gray-600">
          <span>{{ leak.source }}</span>
          <span>{{ leak.date }}</span>
        </div>
      </div>
    </Marquee>

    <!-- Row 2 (reverse) -->
    <Marquee
        reverse
        pause-on-hover
        class="[--duration:35s] mb-1"
    >
      <div
          v-for="leak in secondRow"
          :key="leak.id"
          class="mx-2 bg-white rounded-md border border-gray-200 p-3 min-w-[400px] hover:bg-gray-accent transition-colors"
      >
        <div class="flex items-center space-x-3">
          <img :src="leak.favicon" :alt="leak.alt" class="w-5 h-5 flex-shrink-0">
          <div class="flex-1 min-w-0">
            <p class="font-semibold text-gray-800 truncate text-sm">{{ leak.title }}</p>
            <p class="text-gray-500 truncate text-xs">{{ leak.url }}</p>
          </div>
          <div class="flex items-center space-x-2">
            <span class="text-xs font-semibold px-2 py-1 rounded-full bg-secondary-400 text-white">{{ locale === 'de' ? 'Entfernt' : 'Removed' }}</span>
          </div>
        </div>
        <div class="mt-2 flex items-center justify-between text-xs text-gray-600">
          <span>{{ leak.source }}</span>
          <span>{{ leak.date }}</span>
        </div>
      </div>
    </Marquee>

    <!-- Row 3 -->
    <Marquee
        pause-on-hover
        class="[--duration:28s] mb-1"
    >
      <div
          v-for="leak in thirdRow"
          :key="leak.id"
          class="mx-2 bg-white rounded-md border border-gray-200 p-3 min-w-[400px] hover:bg-gray-accent transition-colors"
      >
        <div class="flex items-center space-x-3">
          <img :src="leak.favicon" :alt="leak.alt" class="w-5 h-5 flex-shrink-0">
          <div class="flex-1 min-w-0">
            <p class="font-semibold text-gray-800 truncate text-sm">{{ leak.title }}</p>
            <p class="text-gray-500 truncate text-xs">{{ leak.url }}</p>
          </div>
          <div class="flex items-center space-x-2">
            <span class="text-xs font-semibold px-2 py-1 rounded-full bg-secondary-400 text-white">{{ locale === 'de' ? 'Entfernt' : 'Removed' }}</span>
          </div>
        </div>
        <div class="mt-2 flex items-center justify-between text-xs text-gray-600">
          <span>{{ leak.source }}</span>
          <span>{{ leak.date }}</span>
        </div>
      </div>
    </Marquee>

    <!-- Row 4 (reverse) -->
    <Marquee
        reverse
        pause-on-hover
        class="[--duration:32s] mb-1"
    >
      <div
          v-for="leak in fourthRow"
          :key="leak.id"
          class="mx-2 bg-white rounded-md border border-gray-200 p-3 min-w-[400px] hover:bg-gray-accent transition-colors"
      >
        <div class="flex items-center space-x-3">
          <img :src="leak.favicon" :alt="leak.alt" class="w-5 h-5 flex-shrink-0">
          <div class="flex-1 min-w-0">
            <p class="font-semibold text-gray-800 truncate text-sm">{{ leak.title }}</p>
            <p class="text-gray-500 truncate text-xs">{{ leak.url }}</p>
          </div>
          <div class="flex items-center space-x-2">
            <span class="text-xs font-semibold px-2 py-1 rounded-full bg-secondary-400 text-white">{{ locale === 'de' ? 'Entfernt' : 'Removed' }}</span>
          </div>
        </div>
        <div class="mt-2 flex items-center justify-between text-xs text-gray-600">
          <span>{{ leak.source }}</span>
          <span>{{ leak.date }}</span>
        </div>
      </div>
    </Marquee>


    <!-- Left Gradient -->
    <div class="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-white"></div>

    <!-- Right Gradient -->
    <div class="pointer-events-none absolute inset-y-0 right-0 w-1/3 bg-gradient-to-l from-white"></div>
  </div>
</template>

<script setup>
import { ref } from "vue";
import Marquee from "./Marquee.vue";

const props = defineProps({
  locale: {
    type: String,
    default: 'en'
  }
});

// Generate fake leak data using "Jane Doe" instead of real names
const generateLeakData = (startId = 1) => {
  const domains = [
    'fapello.com', 'nudostar.com', 'dvir.ru', 'nudogram.com', 'leakshub.com', 
    'onlyleaks.com', 'thothub.to', 'coomer.party', 'kemono.party', 'simpcity.su'
  ];
  
  const sources = [
    props.locale === 'de' ? 'Gefunden auf Website' : 'Found on Website',
    props.locale === 'de' ? 'Gefunden auf Google Images' : 'Found on Google Images',
    props.locale === 'de' ? 'Gefunden auf Bing' : 'Found on Bing',
    props.locale === 'de' ? 'Gefunden auf Google' : 'Found on Google'
  ];

  // Get today's date in DD.MM.YYYY format
  const today = new Date();
  const todayFormatted = today.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  const leaks = [];
  for (let i = 0; i < 8; i++) {
    const domain = domains[i % domains.length];
    const photoNum = String(Math.floor(Math.random() * 100) + 1).padStart(4, '0');
    leaks.push({
      id: `leak-${startId + i}`,
      title: `Jane Doe Nude Leaked OnlyFans Photo #${photoNum} - ${domain.split('.')[0]}`,
      url: `https://${domain}/content/j/a/jane-doe/1000/jane-doe_${photoNum}.jpg`,
      favicon: `https://www.google.com/s2/favicons?domain=https%3A%2F%2F${domain}`,
      alt: `Jane Doe Nude Leaked OnlyFans Photo #${photoNum} - ${domain} favicon`,
      source: sources[Math.floor(Math.random() * sources.length)],
      date: todayFormatted
    });
  }
  return leaks;
};

const firstRow = ref(generateLeakData(1));
const secondRow = ref(generateLeakData(9));
const thirdRow = ref(generateLeakData(17));
const fourthRow = ref(generateLeakData(25));
</script>